import React, { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Pagination } from '../../../clients/quipli-service/pagination/pagination';
import { DateTime } from 'luxon';
import FileSaver from 'file-saver';
import { DailyPaymentReportRequest, DailyPaymentReportRow } from '../../../clients/quipli-service/report/report';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { Card } from '../../../components/card/card';
import { useSearchParams } from 'react-router-dom';
import './payments-report-v2.scss';
import { Paginator } from '../../../components/paginator/paginator-v2';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { displayMoneyFormat } from '../../../util/util';
import classNames from 'classnames';
import { usePaymentConfigApiV1 } from '../../../clients/quipli-service/payment-config/payment-config-api-v1';
import {
  PaymentMethodConfig,
  PaymentMethodConfigType,
} from '../../../clients/quipli-service/payment-config/payment-config';
import { toast } from '../../../util/toasts';
import { SpinnerModal } from '../../../components/modal/spinner-modal';

function extractPaymentReportSearchParams(params: URLSearchParams, defaultFrom: DateTime): DailyPaymentReportRequest {
  return {
    from: params.get('from') ? DateTime.fromISO(params.get('from')!) : defaultFrom,
    to: params.get('to') ? DateTime.fromISO(params.get('to')!) : undefined,
    pageNumber: params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : undefined,
    pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!) : undefined,
  };
}

export const DailyPaymentReport = () => {
  const { dailyPaymentsReport, exportDailyPaymentsReport } = useReportApiV2();
  const { getMultipleConfigurations } = useAPI();
  const { searchPaymentMethods } = usePaymentConfigApiV1();
  const [timezone, setTimezone] = useState<string>();
  const [currency, setCurrency] = useState<string>();
  const [dailyPaymentReport, setDailyPaymentReport] = useState<Pagination<DailyPaymentReportRow>>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodConfig[]>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // List searchRequest hooks
  const [params] = useSearchParams();
  const { request: reportRequest, setRequest: setReportRequest } = useRequestQueryParams<DailyPaymentReportRequest>(
    extractPaymentReportSearchParams(params, DateTime.local({ zone: timezone }).startOf('day')),
    async () => {
      if (timezone && Object.keys(reportRequest).length > 0) {
        setIsLoading(true);
        const report = await dailyPaymentsReport(reportRequest);
        setDailyPaymentReport(report);
        setIsLoading(false);
      }
    },
    [timezone],
  );

  const loadTimezone = async () => {
    const confs = await getMultipleConfigurations(['CONF_TIMEZONE', 'CONF_CURRENCY']);
    const timezone = confs.find((c) => c.variable === 'CONF_TIMEZONE')?.value;
    const currency = confs.find((c) => c.variable === 'CONF_CURRENCY')?.value;
    setCurrency(currency || 'USD');
    if (timezone) {
      setTimezone(timezone);
    }
  };

  const loadPaymentMethods = async () => {
    const methods = await searchPaymentMethods({ includeDeleted: true });
    setPaymentMethods(methods);
  };

  // Hooks
  useEffect(() => {
    loadTimezone();
    loadPaymentMethods();
  }, []);

  const exportReport = async () => {
    setIsExporting(true);
    try {
      const file = await exportDailyPaymentsReport({ from: reportRequest.from, to: reportRequest.to });

      const exportFileName = `Daily_Payments_Report${
        reportRequest.from ? '_' + reportRequest.from?.toFormat('D').replaceAll('/', '-') : ''
      }`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (error) {
      console.error('Error exporting daily payments report', error);
      toast('Error exporting daily payments report', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="container-v2 pb-5">
      <div className="flex justify-between items-center">
        <Typography type="display-xs" weight="medium" className="text-[var(--grey-800)]">
          Daily Payment Totals
        </Typography>
      </div>
      <div className="payments-report-v2 flex flex-column gap-4">
        <div className="payments-report-v2 flex flex-wrap item-start">
          <div className="m-[2px] flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(25%-4px)]">
            <Typography type="text-md" weight="medium" className="text-[var(--grey-700)]">
              From
            </Typography>
            <DatePicker
              value={reportRequest.from}
              onChange={(from) =>
                setReportRequest((prevState) => ({
                  ...prevState,
                  from: from!.setZone(timezone, { keepLocalTime: true }).startOf('day'),
                }))
              }
            />
          </div>
          <div className="m-[2px] flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(25%-4px)]">
            <Typography type="text-md" weight="medium" className="text-[var(--grey-700)]">
              To
            </Typography>
            <DatePicker
              value={reportRequest.to}
              onChange={(to) =>
                setReportRequest((prevState) => ({
                  ...prevState,
                  to: to?.setZone(timezone, { keepLocalTime: true }).endOf('day'),
                }))
              }
              clearable={true}
            />
          </div>
          <div className="m-[2px] self-end flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(50%-4px)]">
            <Button
              className="self-start lg:self-end	button-yellow !border-0 w-[114px] h-[40px]"
              onClick={() => exportReport()}
              data-cy="export"
            >
              <Typography
                type="text-md"
                weight="semi-bold"
                className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
              >
                Export
              </Typography>
              <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
            </Button>
          </div>
        </div>
        <Card>
          <table className="w-full daily-payment-report-table">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Location</th>
                <th className="text-right">Cash</th>
                <th className="text-right">Check</th>
                <th className="text-right">Credit</th>
                <th className="text-right">Bank Account</th>
                <th className="text-right">Credit Memo</th>
                {paymentMethods
                  ?.filter((_) => _.type == PaymentMethodConfigType.Custom)
                  .map((method) => {
                    return (
                      <th key={method.id} className="text-right">
                        {method.displayName}
                      </th>
                    );
                  })}
                <th className="text-right">Other</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={100}>
                    <Spinner />
                  </td>
                </tr>
              ) : (
                <>
                  {currency && dailyPaymentReport && (
                    <>
                      {dailyPaymentReport.items.length > 0 ? (
                        dailyPaymentReport.items.map((report, i) => (
                          <TableRow key={i} report={report} currency={currency} paymentMethods={paymentMethods} />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7}>No record found</td>
                        </tr>
                      )}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
          <Paginator
            pagination={dailyPaymentReport?.pagination}
            request={reportRequest}
            setRequest={(pag) =>
              setReportRequest((req) => {
                return { ...req, ...pag };
              })
            }
          />
        </Card>
      </div>
      <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Daily Payments Report'} />
    </div>
  );
};

const TableRow = ({
  report,
  currency,
  paymentMethods,
}: {
  report: DailyPaymentReportRow;
  currency: string;
  paymentMethods?: PaymentMethodConfig[];
}) => {
  return (
    <>
      {report.LocationTotals.map((lt, i) => {
        return (
          <tr key={`${i}`} className={classNames({ 'location-totals-start': i === 0 })}>
            <td className="Date">{i === 0 ? report.Date : undefined}</td>
            <td className="text-left">{lt.Location}</td>
            <td className="text-right">{displayMoneyFormat(lt.Cash, currency)}</td>
            <td className="text-right">{displayMoneyFormat(lt.Check, currency)}</td>
            <td className="text-right">{displayMoneyFormat(lt.Credit, currency)}</td>
            <td className="text-right">{displayMoneyFormat(lt['Bank Account'], currency)}</td>
            <td className="text-right">{displayMoneyFormat(lt['Credit Memo'], currency)}</td>
            {paymentMethods
              ?.filter((_) => _.type == PaymentMethodConfigType.Custom)
              .map((method) => {
                const total = lt.customTotals?.find((_) => _.name == method.name)?.total ?? 0;
                return (
                  <td key={method.id} className="text-right">
                    {displayMoneyFormat(total, currency)}
                  </td>
                );
              })}
            <td className="text-right">{displayMoneyFormat(lt.Other, currency)}</td>
            <td className="text-right">{displayMoneyFormat(lt.Total, currency)}</td>
          </tr>
        );
      })}
      {report.LocationTotals.length > 1 && (
        <tr>
          <td></td>
          <td></td>
          <td className="text-right font-medium">{displayMoneyFormat(report.Cash, currency)}</td>
          <td className="text-right font-medium">{displayMoneyFormat(report.Check, currency)}</td>
          <td className="text-right font-medium">{displayMoneyFormat(report.Credit, currency)}</td>
          <td className="text-right font-medium">{displayMoneyFormat(report['Bank Account'], currency)}</td>
          <td className="text-right font-medium">{displayMoneyFormat(report['Credit Memo'], currency)}</td>
          {paymentMethods
            ?.filter((_) => _.type == PaymentMethodConfigType.Custom)
            .map((method) => {
              const total = report.customTotals?.find((_) => _.name == method.name)?.total ?? 0;
              return (
                <td key={method.id} className="text-right font-medium">
                  {displayMoneyFormat(total, currency)}
                </td>
              );
            })}
          <td className="text-right font-medium">{displayMoneyFormat(report.Other, currency)}</td>
          <td className="text-right font-medium">{displayMoneyFormat(report.Total, currency)}</td>
        </tr>
      )}
      <tr className={'h-[1.5rem]'}></tr>
    </>
  );
};
